import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import BasicDatepicker from "@/components/Datepicker";
import {
  TIMELINE_LANES,
  TimelineLaneEnum,
} from "@/domain/troubleshooting.enum";
import { TimelineZoom, todayInAccountTz } from "@/utils/troubleshooting.utils";
import { getBackofficeDisplayTimezone } from "@/utils/dateUtils";

interface TimelineToolbarProps {
  zoom: TimelineZoom;
  date: string;
  windowLabel: string;
  /** Empty = all lanes. */
  lanes: TimelineLaneEnum[];
  failuresOnly: boolean;
  isNextDisabled: boolean;
  isFetching?: boolean;
  /** Lane and "failures only" filters only apply to the swimlane. */
  showFilters?: boolean;
  /** Live mode (effective value) and whether it can be turned on. */
  live?: boolean;
  liveAllowed?: boolean;
  onLiveChange?: (live: boolean) => void;
  /** "hh:mm:ss" of the last refresh, shown while Live is on. */
  lastUpdated?: string;
  /** Events that arrived with the last Live refresh. */
  newCount?: number;
  onShowNew?: () => void;
  onZoomChange: (zoom: TimelineZoom) => void;
  onDateChange: (date: string) => void;
  onShift: (direction: 1 | -1) => void;
  onToday: () => void;
  onLanesChange: (lanes: TimelineLaneEnum[]) => void;
  onFailuresOnlyChange: (value: boolean) => void;
  onRefresh: () => void;
}

/**
 * Toggle one lane. The URL keeps "all lanes" as an empty list, at least one
 * lane always stays selected.
 */
export const toggleLane = (
  current: TimelineLaneEnum[],
  lane: TimelineLaneEnum,
): TimelineLaneEnum[] => {
  const selected = current.length === 0 ? TIMELINE_LANES : current;
  const next = selected.includes(lane)
    ? selected.filter((l) => l !== lane)
    : TIMELINE_LANES.filter((l) => l === lane || selected.includes(l));
  if (next.length === 0) return current;
  return next.length === TIMELINE_LANES.length ? [] : next;
};

const TimelineToolbar: React.FC<TimelineToolbarProps> = ({
  zoom,
  date,
  windowLabel,
  lanes,
  failuresOnly,
  isNextDisabled,
  isFetching,
  showFilters = true,
  live = false,
  liveAllowed = false,
  onLiveChange,
  lastUpdated,
  newCount = 0,
  onShowNew,
  onZoomChange,
  onDateChange,
  onShift,
  onToday,
  onLanesChange,
  onFailuresOnlyChange,
  onRefresh,
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const isLaneSelected = (lane: TimelineLaneEnum) =>
    lanes.length === 0 || lanes.includes(lane);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={zoom}
          aria-label={t("toolbar.zoom")}
          onChange={(_, value: TimelineZoom | null) => {
            if (value) onZoomChange(value);
          }}
        >
          <ToggleButton value="day">{t("toolbar.day")}</ToggleButton>
          <ToggleButton value="week">{t("toolbar.week")}</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={t("toolbar.previous")}>
            <IconButton
              aria-label={t("toolbar.previous")}
              onClick={() => onShift(-1)}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, minWidth: 180, textAlign: "center" }}
            data-testid="timeline-window-label"
          >
            {windowLabel}
          </Typography>
          <Tooltip title={t("toolbar.next")}>
            <span>
              <IconButton
                aria-label={t("toolbar.next")}
                onClick={() => onShift(1)}
                disabled={isNextDisabled}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={onToday}>
            {t("toolbar.today")}
          </Button>
        </Box>

        <BasicDatepicker
          label={t("toolbar.date")}
          value={dayjs(date)}
          onChange={(value: Dayjs | null) => {
            if (value && value.isValid()) {
              onDateChange(value.format("YYYY-MM-DD"));
            }
          }}
          disableWrite
          // Days are account-timezone days: today there may already be
          // tomorrow for the browser (e.g. London vs Lima in the evening).
          disableFuture={false}
          maxDate={dayjs(todayInAccountTz())}
          sx={{
            minWidth: 180,
            "& .MuiInputBase-root": { height: 40, maxHeight: 40 },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="timeline-timezone"
        >
          {tt("toolbar.timezone", { tz: getBackofficeDisplayTimezone() })}
        </Typography>

        {showFilters && (
          <FormControlLabel
            control={
              <Switch
                checked={failuresOnly}
                onChange={(e) => onFailuresOnlyChange(e.target.checked)}
              />
            }
            label={t("toolbar.failuresOnly")}
          />
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          disabled={isFetching}
        >
          {t("toolbar.refresh")}
        </Button>

        {onLiveChange && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
            data-testid="timeline-live-controls"
          >
            <Tooltip title={liveAllowed ? "" : t("toolbar.liveDisabled")}>
              <span>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  disabled={!liveAllowed}
                  control={
                    <Switch
                      color="error"
                      checked={live && liveAllowed}
                      onChange={(e) => onLiveChange(e.target.checked)}
                    />
                  }
                  label={t("toolbar.live")}
                />
              </span>
            </Tooltip>
            {live && lastUpdated && (
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="timeline-last-updated"
              >
                {tt("toolbar.lastUpdated", { time: lastUpdated })}
              </Typography>
            )}
            {live && newCount > 0 && onShowNew && (
              <Chip
                size="small"
                color="primary"
                label={tt("toolbar.newEvents", { total: newCount })}
                aria-label={t("toolbar.showNewEvents")}
                onClick={onShowNew}
                data-testid="timeline-new-events"
              />
            )}
          </Box>
        )}
      </Box>

      {showFilters && (
        <Box
          role="group"
          aria-label={t("toolbar.lanes")}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {t("toolbar.lanes")}
          </Typography>
          {TIMELINE_LANES.map((lane) => {
            const selected = isLaneSelected(lane);
            return (
              <Chip
                key={lane}
                label={tt(`lanes.${lane}`)}
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                aria-pressed={selected}
                onClick={() => onLanesChange(toggleLane(lanes, lane))}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TimelineToolbar;
